import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SEOMeta from './SEOMeta';
import { Button } from './ui/button';
import PublicNavbar from './PublicNavbar';
import {
  Building2,
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Tag,
  Share2,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  CheckCircle,
  Lock,
  Mail
} from 'lucide-react';
import { trackBlogPostViewed } from '../services/analytics';
import {
  getPostMeta,
  getAdjacentPosts,
  getRelatedPosts,
  loadPostContent,
} from '../content/blog/queries';

// Still needed for the email gate (blog-access), which is genuinely runtime.
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Simple markdown-like renderer
const renderContent = (content, navigate) => {
  if (!content) return null;

  const lines = content.split('\n');
  const elements = [];
  let inList = false;
  let listItems = [];
  let inTable = false;
  let tableRows = [];
  let tableHeaders = [];

  const processInlineMarkdown = (text) => {
    // Handle bold
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Handle italic
    text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    // Handle links
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
      if (url.startsWith('/')) {
        return `<a href="${url}" class="text-cyan-600 hover:underline" data-internal="true">${linkText}</a>`;
      }
      return `<a href="${url}" class="text-cyan-600 hover:underline" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    });
    // Handle inline code
    text = text.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>');
    return text;
  };

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-2 mb-4 text-gray-700">
          {listItems.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: processInlineMarkdown(item) }} />
          ))}
        </ul>
      );
      listItems = [];
    }
    inList = false;
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} className="overflow-x-auto mb-6">
          <table className="min-w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                {tableHeaders.map((header, i) => (
                  <th key={i} className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-4 py-3 text-sm text-gray-700 border-b" dangerouslySetInnerHTML={{ __html: processInlineMarkdown(cell) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
      tableHeaders = [];
    }
    inTable = false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Skip empty lines but flush lists/tables
    if (trimmedLine === '') {
      flushList();
      flushTable();
      continue;
    }

    // Table row
    if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
      const cells = trimmedLine.slice(1, -1).split('|').map(c => c.trim());

      // Check if it's a separator row (|---|---|)
      if (cells.every(c => /^[-:]+$/.test(c))) {
        continue;
      }

      if (!inTable) {
        inTable = true;
        tableHeaders = cells;
      } else {
        tableRows.push(cells);
      }
      continue;
    } else if (inTable) {
      flushTable();
    }

    // Headers
    if (trimmedLine.startsWith('# ')) {
      flushList();
      elements.push(
        <h1 key={elements.length} className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 mt-8">
          {trimmedLine.slice(2)}
        </h1>
      );
      continue;
    }

    if (trimmedLine.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={elements.length} className="text-2xl font-bold text-gray-900 mb-4 mt-8">
          {trimmedLine.slice(3)}
        </h2>
      );
      continue;
    }

    if (trimmedLine.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={elements.length} className="text-xl font-semibold text-gray-900 mb-3 mt-6">
          {trimmedLine.slice(4)}
        </h3>
      );
      continue;
    }

    // Horizontal rule
    if (trimmedLine === '---') {
      flushList();
      elements.push(<hr key={elements.length} className="my-8 border-gray-200" />);
      continue;
    }

    // List items
    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      inList = true;
      listItems.push(trimmedLine.slice(2));
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(trimmedLine)) {
      inList = true;
      listItems.push(trimmedLine.replace(/^\d+\.\s/, ''));
      continue;
    }

    // If we were in a list but this line isn't a list item, flush the list
    if (inList) {
      flushList();
    }

    // Regular paragraph
    elements.push(
      <p
        key={elements.length}
        className="text-gray-700 leading-relaxed mb-4"
        dangerouslySetInnerHTML={{ __html: processInlineMarkdown(trimmedLine) }}
      />
    );
  }

  // Flush any remaining list/table
  flushList();
  flushTable();

  return elements;
};

// Split content at the <!--GATE--> marker
const splitContent = (raw) => {
  const marker = '<!--GATE-->';
  const idx = raw.indexOf(marker);
  if (idx === -1) return { preview: raw, rest: '' };
  return {
    preview: raw.slice(0, idx).trim(),
    rest:    raw.slice(idx + marker.length).trim(),
  };
};

// Gate UI component
const GateUI = ({ slug, gateStatus, gateEmail, gateError, onEmailChange, onSubmit }) => {
  if (gateStatus === 'sent') {
    return (
      <div className="my-8 bg-white shadow-lg rounded-2xl p-8 text-center border border-gray-100">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Check your inbox!</h3>
        <p className="text-gray-600">
          We sent an access link to <strong>{gateEmail}</strong>.<br />
          Click the link in the email to read the full article.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Fade-out overlay above the gate card */}
      <div className="h-20 -mb-20 relative z-10 bg-gradient-to-b from-transparent to-white pointer-events-none" />

      <div className="my-8 bg-white shadow-lg rounded-2xl p-8 border border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Lock className="h-5 w-5 text-cyan-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">Continue Reading</h3>
        </div>

        <p className="text-gray-600 mb-6">
          Enter your email to unlock the full article. We'll send you a one-time access link.
        </p>

        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            required
            placeholder="your@email.com"
            value={gateEmail}
            onChange={onEmailChange}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={gateStatus === 'submitting'}
            className="px-6 py-3 bg-cyan-600 text-white font-semibold rounded-lg hover:bg-cyan-700 transition disabled:opacity-60 whitespace-nowrap flex items-center gap-2 justify-center"
          >
            <Mail className="h-4 w-4" />
            {gateStatus === 'submitting' ? 'Sending…' : 'Send Access Link'}
          </button>
        </form>

        {gateError && (
          <p className="mt-3 text-sm text-red-600">{gateError}</p>
        )}

        <p className="mt-4 text-xs text-gray-400">No spam. Unsubscribe anytime.</p>
      </div>
    </>
  );
};

const BlogPost = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [content, setContent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [prevPost, setPrevPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);

  // Gate state
  const [gateStatus, setGateStatus] = useState('idle'); // 'idle'|'submitting'|'sent'|'error'
  const [gateEmail, setGateEmail]   = useState('');
  const [gateError, setGateError]   = useState('');
  const [unlocked, setUnlocked]     = useState(false);

  useEffect(() => {
    const loadPost = async () => {
      setLoading(true);
      try {
        const postData = getPostMeta(slug);
        if (!postData) { navigate('/blog'); return; }

        const { prev, next } = getAdjacentPosts(slug);

        setPost(postData);
        setContent(await loadPostContent(slug));
        setRelatedPosts(getRelatedPosts(slug));
        setPrevPost(prev);
        setNextPost(next);

        trackBlogPostViewed(slug, postData.title, postData.category);

        // Gate: check URL token or localStorage
        if (postData.gated) {
          const params = new URLSearchParams(window.location.search);
          const urlToken = params.get('token');
          const savedToken = localStorage.getItem(`blog_unlock_${slug}`);

          if (urlToken) {
            try {
              const validateRes = await fetch(
                `${API_BASE}/api/blog-access/validate?token=${encodeURIComponent(urlToken)}&slug=${encodeURIComponent(slug)}`
              );
              if (validateRes.ok) {
                localStorage.setItem(`blog_unlock_${slug}`, urlToken);
                setUnlocked(true);
              }
            } catch (err) {
              console.error('Token validation error:', err);
            }
          } else if (savedToken) {
            setUnlocked(true);
          }
        }
      } catch (err) {
        console.error('Error loading post:', err);
        navigate('/blog');
      }
      setLoading(false);
    };

    loadPost();
  }, [slug, navigate]);

  const handleGateSubmit = async (e) => {
    e.preventDefault();
    setGateStatus('submitting');
    setGateError('');
    try {
      const res = await fetch(`${API_BASE}/api/blog-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: gateEmail, slug }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setGateStatus('sent');
      } else {
        setGateError(data.error || 'Something went wrong. Please try again.');
        setGateStatus('error');
      }
    } catch {
      setGateError('Network error. Please try again.');
      setGateStatus('error');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async (platform) => {
    const url = window.location.href;
    const title = post?.title || '';

    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Failed to copy:', err);
        }
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  // Determine what content to render
  const renderArticleContent = () => {
    if (post.gated && !unlocked) {
      const { preview } = splitContent(content);
      return (
        <>
          {renderContent(preview, navigate)}
          <GateUI
            slug={slug}
            gateStatus={gateStatus}
            gateEmail={gateEmail}
            gateError={gateError}
            onEmailChange={(e) => setGateEmail(e.target.value)}
            onSubmit={handleGateSubmit}
          />
        </>
      );
    }
    // Unlocked or non-gated: strip the marker if present
    return renderContent(content.replace('<!--GATE-->', ''), navigate);
  };

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    url: `https://nivasapro.ai/blog/${post.slug}`,
    datePublished: post.date,
    author: { '@type': 'Organization', name: 'NivasaPro' },
    publisher: { '@type': 'Organization', name: 'NivasaPro', url: 'https://nivasapro.ai' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://nivasapro.ai/blog/${post.slug}` },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOMeta
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        schema={blogSchema}
      />
      {/* Navigation */}
      <PublicNavbar activeRoute="/blog" />

      {/* Back to Blog */}
      <div className="container mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center text-gray-600 hover:text-cyan-600 transition"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </button>
      </div>

      {/* Article Header */}
      <header className="container mx-auto px-4 pb-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="bg-cyan-100 text-cyan-800 text-sm font-semibold px-3 py-1 rounded-full">
              {post.category}
            </span>
            <span className="text-gray-500 text-sm flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {formatDate(post.date)}
            </span>
            <span className="text-gray-500 text-sm flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {post.readTime}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {post.title}
          </h1>

          <p className="text-xl text-gray-600 mb-6">
            {post.excerpt}
          </p>

          <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-sm text-gray-500 flex items-center"
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 mr-2">Share:</span>
              <button
                onClick={() => handleShare('twitter')}
                className="p-2 rounded-full hover:bg-gray-100 transition"
                title="Share on Twitter"
              >
                <Twitter className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="p-2 rounded-full hover:bg-gray-100 transition"
                title="Share on LinkedIn"
              >
                <Linkedin className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={() => handleShare('copy')}
                className="p-2 rounded-full hover:bg-gray-100 transition"
                title="Copy link"
              >
                {copied ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <LinkIcon className="h-5 w-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="container mx-auto px-4 pb-16">
        <div className="max-w-3xl mx-auto prose prose-lg">
          {renderArticleContent()}
        </div>
      </article>

      {/* Author Box */}
      <section className="container mx-auto px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Building2 className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{post.author}</h3>
                <p className="text-gray-600 text-sm">
                  Property owners and tech enthusiasts building the future of rental management.
                  20+ years of Central Ohio landlord experience, now powered by AI.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Post Navigation */}
      <section className="container mx-auto px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-between gap-4">
            {prevPost ? (
              <button
                onClick={() => navigate(`/blog/${prevPost.slug}`)}
                className="flex-1 p-4 rounded-xl border border-gray-200 hover:border-cyan-300 hover:shadow-md transition text-left group"
              >
                <span className="text-sm text-gray-500 flex items-center mb-1">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Previous
                </span>
                <span className="font-medium text-gray-900 group-hover:text-cyan-600 transition line-clamp-1">
                  {prevPost.title}
                </span>
              </button>
            ) : <div className="flex-1" />}

            {nextPost ? (
              <button
                onClick={() => navigate(`/blog/${nextPost.slug}`)}
                className="flex-1 p-4 rounded-xl border border-gray-200 hover:border-cyan-300 hover:shadow-md transition text-right group"
              >
                <span className="text-sm text-gray-500 flex items-center justify-end mb-1">
                  Next
                  <ArrowRight className="h-4 w-4 ml-1" />
                </span>
                <span className="font-medium text-gray-900 group-hover:text-cyan-600 transition line-clamp-1">
                  {nextPost.title}
                </span>
              </button>
            ) : <div className="flex-1" />}
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="container mx-auto px-4 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost) => (
                <article
                  key={relatedPost.slug}
                  className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition cursor-pointer group"
                  onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                >
                  <span className="text-xs text-gray-500 flex items-center mb-2">
                    <Clock className="h-3 w-3 mr-1" />
                    {relatedPost.readTime}
                  </span>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-cyan-600 transition line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{relatedPost.excerpt}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-600 to-blue-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Put These Ideas Into Action?
          </h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            Join the waitlist for NivasaPro.ai and be among the first to experience AI-powered property management.
          </p>
          <Button
            size="lg"
            className="bg-white text-cyan-700 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
            onClick={() => navigate('/')}
          >
            Get Early Access <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-6 w-6" />
                <span className="text-xl font-bold text-white">NivasaPro.ai</span>
              </div>
              <p className="text-sm text-gray-400">
                Agentic rental property management powered by AI.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/features')} className="hover:text-cyan-400 transition text-left">Features</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Pricing</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Security</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-cyan-400 transition text-left">About</button></li>
                <li><button onClick={() => navigate('/blog')} className="hover:text-cyan-400 transition text-left">Blog</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Careers</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-cyan-400 transition text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-cyan-400 transition text-left">Terms of Service</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Contact</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2026 NivasaPro.ai. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
